import type { SiteId } from './data/sites';
import type { FishSpeciesId, IslandDay } from './fish';
import type { EatenBy, Hunt } from './predation';

/**
 * Why the animals die, as it happens (M80, after Sable Reserve's M297): a
 * listener the simulation tells of each stock's day, its dead by cause, in
 * fish and kg, and its settlers, by species, place, island and day; and
 * since M84 its dead eaten, by the predator named (each predator its part,
 * M126), and a predator's hunt.
 *
 * **A diagnostic, not a feature.** It is there so that a question like "what
 * takes Maui Nui's bullethead, and in which months?" is answered by reading
 * the deaths rather than by patching a copy of the model to count them.
 * `scripts/deathaudit.ts` attaches a listener and prints the books; nothing in
 * the game attaches one.
 *
 * With no listener attached the fish's step is handed nothing to tell and
 * keeps nothing: nothing is saved and no random number is drawn, so a world
 * runs to the bit as it would without this file. That is the contract, and
 * `tests/deathAudit.test.ts` holds it on the fingerprint.
 *
 * Counts are fractional: a class's dead are one draw at the sum of its
 * natural deaths, its fishing, its predation and its crowding (`fish.ts`), and they are
 * shared out by each cause's part of that sum, each cause's chance of having
 * taken a fish that died under the rates held through the day.
 */

/** How a fish died: natural deaths (Hamel and Cope's rate), fishing, eaten by a predator the game holds (M84; Sable Reserve's `predation`), or the young's crowding (M84). */
export const DEATH_CAUSES = ['natural', 'fished', 'predation', 'crowding'] as const;

/** A stock's day, as the audit is told it: its predation's dead eaten, each predator its part, and a predator's own hunt. */
export interface StockDay extends IslandDay {
  species: FishSpeciesId;
  siteId: SiteId;
  epochDay: number;
  /** The species that ate its dead `predation`, each its part of them (M126; the parts summing to 1), or none where none preys on it. */
  eatenBy: readonly EatenBy[];
  /** A predator's day on the island: its need, the prey it met and its other prey (`predation.ts`). */
  hunt?: Hunt;
}

export interface DeathAudit {
  stock(day: StockDay): void;
}

let listener: DeathAudit | null = null;

/** Attach a listener, or `null` to detach. Returns the one it replaced. */
export function setDeathAudit(next: DeathAudit | null): DeathAudit | null {
  const before = listener;
  listener = next;
  return before;
}

/** The listener attached, if any. */
export function deathAudit(): DeathAudit | null {
  return listener;
}
